// Building kit: loads the sci-fi/desert low-poly GLB. The 35 buildings are exported as
// hundreds of loose panels/blocks, so we (1) split every mesh into pieces, (2) CLUSTER the
// pieces by XZ proximity back into whole buildings, then hand out a size-appropriate
// building per amenity type. The amenity dress-up layer recolors + decorates them.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { getBakedBuildingAssets } from './buildingConfig.js';

let COMPONENTS = null;   // sorted ascending by footprint: [{ geo, w, h, d, foot }]
let ALL_COMPONENTS = null; // every plausible piece (for the dev mesh palette)
let SRC_MAT = null;
let loadPromise = null;

async function loadGLTFWithRetry(url, attempts = 3) {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await new GLTFLoader().loadAsync(url);
    } catch (error) {
      lastError = error;
      await new Promise((resolve) => setTimeout(resolve, 250 * (i + 1)));
    }
  }
  throw lastError;
}

// Split a geometry into its loose pieces (kept in WORLD coords, not recentered).
function splitPieces(geo) {
  const pos = geo.attributes.position;
  const idx = geo.index ? geo.index.array : null;
  const triCount = idx ? idx.length / 3 : pos.count / 3;
  const n = pos.count;
  const weld = new Int32Array(n);
  const map = new Map();
  let wc = 0;
  for (let i = 0; i < n; i++) {
    const k = Math.round(pos.getX(i) * 50) + '_' + Math.round(pos.getY(i) * 50) + '_' + Math.round(pos.getZ(i) * 50);
    let w = map.get(k);
    if (w === undefined) { w = wc++; map.set(k, w); }
    weld[i] = w;
  }
  const parent = new Int32Array(wc);
  for (let i = 0; i < wc; i++) parent[i] = i;
  const find = (a) => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
  const uni = (a, b) => { a = find(a); b = find(b); if (a !== b) parent[b] = a; };
  const tv = (t) => idx ? [idx[t * 3], idx[t * 3 + 1], idx[t * 3 + 2]] : [t * 3, t * 3 + 1, t * 3 + 2];
  for (let t = 0; t < triCount; t++) { const [a, b, c] = tv(t); uni(weld[a], weld[b]); uni(weld[b], weld[c]); }
  const groups = new Map();
  for (let t = 0; t < triCount; t++) { const r = find(weld[tv(t)[0]]); (groups.get(r) || groups.set(r, []).get(r)).push(t); }
  const hasUV = !!geo.attributes.uv, su = geo.attributes.uv;
  const pieces = [];
  for (const tris of groups.values()) {
    const positions = new Float32Array(tris.length * 9);
    const uvs = hasUV ? new Float32Array(tris.length * 6) : null;
    let o = 0, uo = 0;
    const bb = new THREE.Box3();
    const v = new THREE.Vector3();
    for (const t of tris) for (const vi of tv(t)) {
      const x = pos.getX(vi), y = pos.getY(vi), z = pos.getZ(vi);
      positions[o++] = x; positions[o++] = y; positions[o++] = z;
      bb.expandByPoint(v.set(x, y, z));
      if (hasUV) { uvs[uo++] = su.getX(vi); uvs[uo++] = su.getY(vi); }
    }
    pieces.push({ positions, uvs, bb });
  }
  return pieces;
}

// Cluster pieces whose XZ footprints touch (same building), separated buildings stay apart.
function clusterPieces(pieces, gap) {
  const N = pieces.length;
  const parent = new Int32Array(N);
  for (let i = 0; i < N; i++) parent[i] = i;
  const find = (a) => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
  // expanded XZ rectangles
  const rects = pieces.map((p) => ({ x0: p.bb.min.x - gap, x1: p.bb.max.x + gap, z0: p.bb.min.z - gap, z1: p.bb.max.z + gap }));
  for (let i = 0; i < N; i++) {
    const a = rects[i];
    for (let j = i + 1; j < N; j++) {
      const b = rects[j];
      if (a.x0 <= b.x1 && a.x1 >= b.x0 && a.z0 <= b.z1 && a.z1 >= b.z0) {
        const ra = find(i), rb = find(j); if (ra !== rb) parent[rb] = ra;
      }
    }
  }
  const clusters = new Map();
  for (let i = 0; i < N; i++) { const r = find(i); (clusters.get(r) || clusters.set(r, []).get(r)).push(i); }
  return [...clusters.values()];
}

function mergeCluster(pieces, ids) {
  let total = 0, hasUV = true;
  for (const id of ids) { total += pieces[id].positions.length; if (!pieces[id].uvs) hasUV = false; }
  const positions = new Float32Array(total);
  const uvs = hasUV ? new Float32Array(total / 3 * 2) : null;
  let o = 0, uo = 0;
  const bb = new THREE.Box3();
  for (const id of ids) {
    const p = pieces[id];
    positions.set(p.positions, o); o += p.positions.length;
    if (hasUV) { uvs.set(p.uvs, uo); uo += p.uvs.length; }
    bb.union(p.bb);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  if (hasUV) g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  // recenter: footprint centered, base at y=0
  const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
  g.translate(-cx, -bb.min.y, -cz);
  g.computeVertexNormals();
  const w = bb.max.x - bb.min.x, h = bb.max.y - bb.min.y, d = bb.max.z - bb.min.z;
  return { geo: g, w, h, d, foot: Math.max(w, d) };
}

export function initBuildingKit() {
  if (loadPromise) return loadPromise;
  loadPromise = loadGLTFWithRetry('uploads/buildings.glb').then(async (gltf) => {
    gltf.scene.updateMatrixWorld(true);
    let pieces = [];
    gltf.scene.traverse((o) => {
      if (!o.isMesh) return;
      if (!SRC_MAT) SRC_MAT = o.material;
      const g = o.geometry.clone().applyMatrix4(o.matrixWorld);
      pieces = pieces.concat(splitPieces(g));
      g.dispose();
    });
    // gap ~ 1.6 world units merges a building's panels but keeps neighbours apart
    const clusters = clusterPieces(pieces, 1.6);
    let comps = clusters.map((ids) => mergeCluster(pieces, ids));
    // keep EVERY plausible building piece available for the dev mesh palette
    ALL_COMPONENTS = comps.filter((c) => c.h > 0.8 && c.foot > 1).sort((a, b) => a.foot - b.foot);
    // drop the ground slab (huge foot) and specks
    const foots = comps.map((c) => c.foot).sort((a, b) => a - b);
    const med = foots[Math.floor(foots.length / 2)] || 10;
    COMPONENTS = comps
      .filter((c) => c.foot >= med * 0.35 && c.foot <= med * 3.5 && c.h > 1.5)
      .sort((a, b) => a.foot - b.foot);
    if (COMPONENTS.length < 10) COMPONENTS = comps.filter((c) => c.h > 1.5).sort((a, b) => a.foot - b.foot);
    await Promise.all(Object.entries(getBakedBuildingAssets()).map(([k, url]) =>
      loadGLTFWithRetry(url).then((g) => {
        g.scene.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
        // sit the baked model on the ground: base at y=0, centred on x/z
        const wrap = new THREE.Group();
        wrap.add(g.scene);
        const bb = new THREE.Box3().setFromObject(g.scene);
        // per-model: put THIS model's exact lowest point on the ground (no global guess)
        g.scene.position.set(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2);
        BAKED_CACHE[k] = wrap;
      }).catch((e) => console.warn('[bake] load failed', k, e))
    ));
    await loadVases();
    await loadLamps();
    await loadCrates();
    return COMPONENTS;
  });
  return loadPromise;
}

export function getComponents() { return COMPONENTS; }
export function getAllComponents() { return ALL_COMPONENTS || COMPONENTS || []; }
export function getKitMaterialColor() { return 0xe4cf9e; }

let CRATES = [];
export function getCrate(i) { return CRATES.length ? CRATES[((i % CRATES.length) + CRATES.length) % CRATES.length] : null; }
export async function loadCrates() {
  if (CRATES.length) return CRATES;
  try {
    const fbx = await new FBXLoader().loadAsync('uploads/crates.fbx');
    fbx.updateMatrixWorld(true);
    fbx.traverse((o) => {
      if (!o.isMesh || !o.geometry) return;
      const geo = o.geometry.clone().applyMatrix4(o.matrixWorld);
      geo.computeBoundingBox();
      const bb = geo.boundingBox;
      geo.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2);
      geo.computeVertexNormals();
      CRATES.push({ geo, h: Math.max(0.01, bb.max.y - bb.min.y) });
    });
    CRATES.sort((a, b) => a.h - b.h);
  } catch (e) { console.warn('[crates] load failed', e); }
  return CRATES;
}

// Clay vase pack (dress-up pots). Each vase recentred with base at y=0.
let VASES = [];
export function getVase(i) { return VASES.length ? VASES[((i % VASES.length) + VASES.length) % VASES.length] : null; }
export async function loadVases() {
  if (VASES.length) return VASES;
  try {
    const fbx = await new FBXLoader().loadAsync('uploads/clay_vases.fbx');
    fbx.updateMatrixWorld(true);
    const pieces = [];
    fbx.traverse((o) => { if (o.isMesh) { const g = o.geometry.clone().applyMatrix4(o.matrixWorld); splitPieces(g).forEach((p) => pieces.push(p)); g.dispose(); } });
    VASES = pieces.filter((p) => (p.bb.max.y - p.bb.min.y) > 0.05).map((p) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(p.positions, 3));
      geo.translate(-(p.bb.min.x + p.bb.max.x) / 2, -p.bb.min.y, -(p.bb.min.z + p.bb.max.z) / 2);
      geo.computeVertexNormals();
      return { geo, h: p.bb.max.y - p.bb.min.y };
    }).sort((a, b) => a.h - b.h);
  } catch (e) { console.warn('[vases] load failed', e); }
  return VASES;
}

let LAMPS = [];
export function getLamp(i) { return LAMPS.length ? LAMPS[((i % LAMPS.length) + LAMPS.length) % LAMPS.length] : null; }
export async function loadLamps() {
  if (LAMPS.length) return LAMPS;
  try {
    const fbx = await new FBXLoader().loadAsync('uploads/lamps.fbx');
    fbx.updateMatrixWorld(true);
    const pieces = [];
    fbx.traverse((o) => { if (o.isMesh) { const g = o.geometry.clone().applyMatrix4(o.matrixWorld); splitPieces(g).forEach((p) => pieces.push(p)); g.dispose(); } });
    LAMPS = pieces.filter((p) => (p.bb.max.y - p.bb.min.y) > 0.15).map((p) => {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(p.positions, 3));
      geo.translate(-(p.bb.min.x + p.bb.max.x) / 2, -p.bb.min.y, -(p.bb.min.z + p.bb.max.z) / 2);
      geo.computeVertexNormals();
      return { geo, h: p.bb.max.y - p.bb.min.y };
    }).sort((a, b) => b.h - a.h);
  } catch (e) { console.warn('[lamps] load failed', e); }
  return LAMPS;
}

const BAKED_CACHE = {};
export function getBakedModel(type) { return BAKED_CACHE[type] ? BAKED_CACHE[type].clone(true) : null; }

// A clone of the kit's sandstone material, gently sand-tinted (for custom entry geometry).
export function getKitMaterial() {
  const mat = SRC_MAT ? SRC_MAT.clone() : new THREE.MeshStandardMaterial({ roughness: 0.95 });
  mat.color = new THREE.Color(0xe4cf9e);
  mat.roughness = 0.95; mat.metalness = 0.0;
  if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
  return mat;
}

function hash(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; }
function tierFor(fp) {
  const area = fp[0] * fp[1];
  if (area >= 6) return 'huge';
  if (area >= 4) return 'big';
  if (area >= 2) return 'mid';
  return 'small';
}
function pickComponent(type, tier) {
  const N = COMPONENTS.length;
  const bands = { small: [0.0, 0.4], mid: [0.35, 0.62], big: [0.55, 0.82], huge: [0.72, 1.0] };
  const [lo, hi] = bands[tier] || bands.small;
  const a = Math.floor(lo * N), b = Math.max(a + 1, Math.floor(hi * N));
  return COMPONENTS[a + (hash(type) % (b - a))];
}

// Desert/savanna palette bands, keyed to height fraction of the building.
function bakePartColors(srcGeo, accent) {
  const geo = srcGeo.clone();
  const pos = geo.attributes.position;
  geo.computeBoundingBox();
  const bb = geo.boundingBox;
  const y0 = bb.min.y, hh = Math.max(0.001, bb.max.y - bb.min.y);
  const n = pos.count;
  const colAttr = new Float32Array(n * 3);
  const sand = new THREE.Color(0xe9d6ac);       // main sandstone
  const sandHi = new THREE.Color(0xf4e6c4);      // sun-bleached top
  const plinth = new THREE.Color(0xb8936a);      // clay foundation
  const band = new THREE.Color(accent).lerp(new THREE.Color(0xcaa76a), 0.35); // painted frieze
  const shade = new THREE.Color(0x9c7c4e);       // recessed / shadowed
  const c = new THREE.Color();
  // face-normal driven AO-ish darkening: compute per triangle (non-indexed geo)
  const v = new THREE.Vector3(), a = new THREE.Vector3(), b = new THREE.Vector3(), cc = new THREE.Vector3(), nrm = new THREE.Vector3();
  for (let t = 0; t < n; t += 3) {
    a.set(pos.getX(t), pos.getY(t), pos.getZ(t));
    b.set(pos.getX(t + 1), pos.getY(t + 1), pos.getZ(t + 1));
    cc.set(pos.getX(t + 2), pos.getY(t + 2), pos.getZ(t + 2));
    nrm.crossVectors(b.clone().sub(a), cc.clone().sub(a)).normalize();
    const up = Math.max(0, nrm.y);            // upward faces catch light
    for (let k = 0; k < 3; k++) {
      const i = t + k;
      const fy = (pos.getY(i) - y0) / hh;      // 0 base .. 1 top
      // choose band by height
      if (fy < 0.14) c.copy(plinth);
      else if (fy > 0.86) c.copy(sandHi);
      else if (fy > 0.62 && fy < 0.74) c.copy(band);   // decorative frieze near the top
      else c.copy(sand);
      // blend toward accent tint slightly + AO from facing
      c.lerp(new THREE.Color(accent), 0.12);
      c.lerp(shade, (1 - up) * 0.18);
      // subtle per-triangle grain
      const g = 0.94 + ((t * 13 % 11) / 11) * 0.1;
      colAttr[i * 3] = c.r * g; colAttr[i * 3 + 1] = c.g * g; colAttr[i * 3 + 2] = c.b * g;
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colAttr, 3));
  return geo;
}

export function makeBuildingMesh(type, cellSize, fp, accent) {
  if (!COMPONENTS || !COMPONENTS.length) return null;
  const tier = tierFor(fp);
  const comp = pickComponent(type, tier);
  const targetFoot = Math.max(fp[0], fp[1]) * cellSize * 0.94;
  const maxH = { small: 4.5, mid: 6, big: 8, huge: 11 }[tier] || 4.5;
  const s = Math.min(targetFoot / comp.foot, maxH / comp.h);
  const geo = bakePartColors(comp.geo, accent);
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.96, metalness: 0.0, flatShading: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.scale.setScalar(s);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.buildH = comp.h * s;   // scaled height, for placing decorations above the roof/entrance
  return mesh;
}
